import React from 'react';
import cn from 'classnames';
import ProgressiveBackground from '@ifit/mp-progressive-background';
import Chip from '@ifit/mp-chip';
import LineWrap from '@ifit/mp-line-wrap';
import { formatters } from '@ifit/mp-estimates';
import NativeClick, { METHODS } from '@ifit/mp-native-click';

import './index.scss';

interface IOwnProps {
  featuredItem: IFeaturedContentSection;
  equipmentType?: string;
  deviceType?: string;
  useMetricUnits: boolean;
  classNames?: string;
  i18n: any;
  themeClass: string;
}

export interface IFeaturedContentSection {
  ids: Array<string>;
  type: string;
  title: string;
  description: string;
  backgroundImage: string;
  items: Array<IFeaturedContentSectionItem>;
}

interface IFeaturedContentSectionItem {
  type: string;
  item: {
    itemId: string;
    image: string;
    title: string;
    workouts?: Array<string>;
    estimates?: {
      time?: number;
      distance?: number;
    };
  };
}

interface IProps extends IOwnProps {}

const PureFeaturedContentSection: React.FC<IProps> = ({
  featuredItem,
  i18n,
  useMetricUnits,
  equipmentType,
  deviceType,
}) => {
  if (!featuredItem?.items) {
    return null;
  }

  const numberOfWorkoutsLabel = count =>
    i18n._translate('%{count} Workout', {
      plural: '%{count} Workouts',
      context:
        'Label to tell the user how many workouts are in a series. A series is a set of related workouts that the user completes over several weeks.',
      count,
    });

  const isMobile = deviceType === 'mobile';
  const titleBox = (
    <div
      className={cn(!isMobile ? 'section-scrollable-item' : '', 'title-box')}
    >
      <LineWrap
        className="title"
        maxLines={2}
        text={featuredItem.title}
        recalculateOnResize
      />
      <LineWrap
        className="description"
        maxLines={2}
        text={featuredItem.description}
        recalculateOnResize
      />
    </div>
  );

  return (
    <div className={`spotlight`}>
      {featuredItem.backgroundImage && (
        <ProgressiveBackground
          resizeOptions={{ useStandardDimensions: false }}
          image={featuredItem.backgroundImage}
        />
      )}
      <div className="image-overlay" />
      <div className="content">
        {isMobile && titleBox}
        <div className="section-scrollable-content">
          {!isMobile && titleBox}
          {featuredItem.items.map((data, index) => {
            let item = data.item;
            if (data.type === 'program') {
              return (
                <NativeClick
                  objectToSend={METHODS.programSelected(item.itemId)}
                  key={`${item.itemId}-${index}`}
                >
                  <div className="section-scrollable-item">
                    <div className="image-overlay"></div>
                    <ProgressiveBackground
                      image={item.image}
                      resizeOptions={{ useStandardDimensions: false }}
                    />
                    <Chip>{numberOfWorkoutsLabel(item.workouts?.length)}</Chip>
                    <LineWrap
                      className="card-title"
                      maxLines={3}
                      text={item.title}
                      recalculateOnResize
                    />
                  </div>
                </NativeClick>
              );
            } else {
              return (
                <NativeClick
                  objectToSend={METHODS.workoutSelected(item.itemId)}
                  key={`${item.itemId}-${index}`}
                >
                  <div className="section-scrollable-item">
                    <div className="image-overlay"></div>
                    <ProgressiveBackground
                      image={item.image}
                      resizeOptions={{ useStandardDimensions: false }}
                    />
                    <div className="chips">
                      {item.estimates && item.estimates.time && (
                        <Chip>
                          {formatters(i18n)['time'](item.estimates.time)}
                        </Chip>
                      )}
                      {item.estimates && item.estimates.distance && (
                        <Chip>
                          {formatters(i18n)['distance'](
                            item.estimates.distance,
                            useMetricUnits,
                            equipmentType
                          )}
                        </Chip>
                      )}
                    </div>
                    <LineWrap
                      className="card-title"
                      maxLines={3}
                      text={item.title}
                      recalculateOnResize
                    />
                  </div>
                </NativeClick>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default PureFeaturedContentSection;
